import { ScheduleConflictItem, ScheduleWorkingHoursViolation } from "@myapp/contract";
import { parseCompletedBlockersFromError } from "./parseCompletedBlockersFromError";
import { parseWorkingHoursViolationsFromError } from "./parseWorkingHoursViolationsFromError";
import { parseConflictsFromError } from "./parseConflictsFromError";

/**
 * The three flavours of 409 a scheduling route answers with, as one value.
 *
 * `blockers` is completed work in the range and is **never** overridable — no flag gets past it.
 * `workingHours` means the technician does not work then and is waived with
 * `allowOutsideWorkingHours`. `conflict` carries the overlapping entries and is waived with
 * `force`, which double-books and flags the displaced entries for Umdisponierung.
 */
export type ScheduleRejection =
  | { kind: "blockers"; items: ScheduleConflictItem[] }
  | { kind: "workingHours"; items: ScheduleWorkingHoursViolation[] }
  | { kind: "conflict"; items: ScheduleConflictItem[] };

/**
 * Returns null for anything that is not one of the three — an ordinary error the caller shows
 * inline.
 *
 * The order below is the API's own check order (`SchedulingService.validate`) and is the reason
 * this lives in one place: a caller that tested for overlaps first would offer "save anyway" for
 * a range that is actually blocked by completed work.
 */
export function parseScheduleRejection(error: unknown): ScheduleRejection | null {
  const blockers = parseCompletedBlockersFromError(error);
  if (blockers) return { kind: "blockers", items: blockers };

  const violations = parseWorkingHoursViolationsFromError(error);
  if (violations) return { kind: "workingHours", items: violations };

  const conflicts = parseConflictsFromError(error);
  if (conflicts) return { kind: "conflict", items: conflicts };

  return null;
}
